import { useState } from 'react';
import { match } from 'ts-pattern';

import {
  Badge,
  HStack,
  InputLabel,
  Switch,
  Text,
  VStack,
} from '@eluve/components';
import { useAppointmentMode } from '@eluve/frontend-appointment-hooks';
import { DbEnums } from '@eluve/graphql-types';
import { useAnalytics } from '@eluve/posthog-react';
import {
  useAppointmentTemplates,
  useIsFeatureFlagEnabled,
} from '@eluve/smart-blocks';

import { OutputTemplateSelector } from './OutputTemplateSelector';
import { TemplateSelectorGuard } from './TemplateSelectorGuard';

interface NewAppointmentTemplateProps {
  tenantId: string;
  appointmentId: string;
  showHeader?: boolean;
  usesPortal?: boolean;
  side?: 'bottom' | 'left' | 'right' | 'top';
}

export const NewAppointmentTemplateSelector: React.FC<
  NewAppointmentTemplateProps
> = ({ tenantId, appointmentId, showHeader = true, usesPortal, side }) => {
  const {
    initialLlmOutputTemplateId,
    initialPromptTemplateId,
    updateAppointmentTemplates,
    updateAppointmentOutputTemplate,
  } = useAppointmentTemplates();
  const analytics = useAnalytics('appointment-template-selector');

  const isAutoTemplateEnabled = useIsFeatureFlagEnabled(
    'AUTO_RECOMMEND_PROMPT_TEMPLATE',
  );

  const appointmentMode = useAppointmentMode();

  // AI-Optimized toggle state - true when both template IDs are null (AI will recommend)
  const [isAiOptimized, setIsAiOptimized] = useState(
    isAutoTemplateEnabled &&
      !initialPromptTemplateId &&
      !initialLlmOutputTemplateId,
  );

  const handleAiOptimizedToggle = (checked: boolean) => {
    setIsAiOptimized(checked);
    analytics.capture('ai_optimized_template_toggle', {
      tenantId,
      appointmentId,
      checked,
    });

    if (checked && appointmentMode === DbEnums.AppointmentMode.AUDIO_COPILOT) {
      updateAppointmentTemplates({
        variables: {
          tenantId,
          id: appointmentId,
          initialPromptTemplateId: null,
          initialLlmOutputTemplateId: null,
        },
      });
    }
    // When AI-Optimized is disabled, the template selectors will handle setting defaults
  };

  return match(appointmentMode)
    .with(DbEnums.AppointmentMode.AUDIO_COPILOT, () => (
      // In audio copilot mode, we need to select both the prompt and output templates
      <InputLabel label={showHeader ? 'Template' : null}>
        <VStack gap={3}>
          {/* AI-Optimized Template Toggle */}
          {isAutoTemplateEnabled && (
            <HStack
              justify="between"
              className="rounded-md border border-border-primary p-2"
            >
              <HStack>
                <Switch
                  checked={isAiOptimized}
                  onCheckedChange={handleAiOptimizedToggle}
                />
                <Text size="md">AI-Optimized Template Selection</Text>
              </HStack>

              <Badge variant="success">Recommended</Badge>
            </HStack>
          )}

          {/* Show template selectors only when AI-Optimized is disabled */}
          {!isAiOptimized && (
            <TemplateSelectorGuard
              selectedPromptTemplateId={initialPromptTemplateId ?? undefined}
              onSelectionChanged={({
                initialPromptTemplateId,
                initialOutputTemplateId,
              }) =>
                updateAppointmentTemplates({
                  variables: {
                    tenantId,
                    id: appointmentId,
                    initialPromptTemplateId,
                    initialLlmOutputTemplateId: initialOutputTemplateId,
                  },
                })
              }
              selectedOutputTemplateId={initialLlmOutputTemplateId ?? undefined}
              usesPortal={usesPortal}
              side={side}
            />
          )}
        </VStack>
      </InputLabel>
    ))
    .with(DbEnums.AppointmentMode.MANUAL_CHARTING, () => (
      // In manual charting mode, we only need to select the output template
      <InputLabel label={showHeader ? 'Output Template' : null}>
        <OutputTemplateSelector
          selectedOutputTemplateId={initialLlmOutputTemplateId ?? undefined}
          onSelectionChanged={(initialOutputTemplateId) =>
            updateAppointmentOutputTemplate({
              variables: {
                tenantId,
                id: appointmentId,
                initialLlmOutputTemplateId: initialOutputTemplateId,
              },
            })
          }
          usesPortal={usesPortal}
          side={side}
        />
      </InputLabel>
    ))
    .exhaustive();
};
